import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { Droplets, MessageCircle, CalendarCheck, Receipt, ArrowRight, Repeat, Sparkles, MapPin, Mail, Send } from "lucide-react";
import { bk, nok } from "@/lib/booking";
import { useSeo, businessLd } from "@/lib/seo";
import { CtaButtons } from "@/components/site/SiteLayout";
import { ComingSoon } from "@/components/site/Brand";

export const priceText = (s) => !s.bookable ? "Pris etter befaring / fast avtale"
  : s.unit === "vindu" ? `Fra ${nok(s.unit_price)} per vindu` : `${nok(s.price_sqm)}/m² · min. ${nok(s.min_price)}`;

export const ServiceCard = ({ s }) => (
  <Link to={`/tjenester/${s.slug}`} data-testid={`service-card-${s.slug}`}
    className="group rounded-2xl border border-stone-200 bg-white p-5 hover:border-[#2F5D50]/40 hover:-translate-y-0.5 transition-[transform,border-color]">
    <h3 className="font-heading font-bold text-stone-900">{s.name}</h3>
    <p className="mt-1.5 text-sm text-stone-600 line-clamp-2">{s.description}</p>
    <div className="mt-4 flex items-center text-sm">
      <span className="font-semibold text-[#2F5D50]">{priceText(s)}</span>
      <ArrowRight className="ml-auto h-4 w-4 text-stone-400 group-hover:translate-x-1 group-hover:text-[#2F5D50] transition-transform" />
    </div>
  </Link>
);

const Point = ({ icon: Icon, title, text }) => (
  <div>
    <Icon className="h-6 w-6 text-[#2F5D50]" aria-hidden />
    <h3 className="mt-3 font-heading font-bold text-stone-900">{title}</h3>
    <p className="mt-1 text-sm text-stone-600">{text}</p>
  </div>
);

export default function Home() {
  const cfg = useOutletContext();
  const [cat, setCat] = useState(null);
  useEffect(() => { bk.catalog().then(setCat); }, []);
  useSeo({ title: "Ask-Stokke Renhold – Renhold i Asker, Bærum og Oslo", path: "/",
    description: "Husvask, flyttevask, Airbnb-vask og fast renhold i Asker, Bærum, Oslo og Drammen. Se pris før du bestiller og velg blant ekte ledige tider.",
    jsonLd: cfg ? businessLd(cfg) : null });

  return (
    <main data-testid="home-page">
      <section className="mx-auto grid max-w-6xl items-center gap-10 px-5 pt-12 pb-16 lg:grid-cols-[1.1fr_1fr] lg:pt-20">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#E7EFE9] px-3 py-1 text-xs font-bold text-[#2F5D50]">
            <Droplets className="h-3.5 w-3.5" /> Vann og mikrofiber der det passer
          </span>
          <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight text-stone-900">
            Rent hjem. Mindre unødvendig kjemi.
          </h1>
          <p className="mt-5 max-w-xl text-base sm:text-lg text-stone-600">
            Vi prioriterer vann, mikrofiber og gjennomtenkte rengjøringsmetoder for et rent hjem – med omtanke for mennesker, kjæledyr og overflater.
          </p>
          <div className="mt-8"><CtaButtons /></div>
          <p className="mt-4 text-sm font-semibold text-[#2F5D50]">Bestill på 2 minutter – så står renholderen på døren.</p>
          <p className="mt-1 text-xs text-stone-500">Konkurransedyktige priser · Totalpris før du bestiller · Ingen skjulte gebyrer</p>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.1 }}>
          <ComingSoon className="aspect-[4/3] w-full shadow-xl shadow-stone-300/40" label="Eget bilde fra Ask-Stokke Renhold" />
        </motion.div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
        <Point icon={Receipt} title="Tydelige priser" text="Pris per m² med minstepris. Du ser hva som er inkludert og hva tillegg koster." />
        <Point icon={MessageCircle} title="Du blir hørt" text="Fortell oss hva som er viktig – rom, rekkefølge, produkter, allergier og kjæledyr." />
        <Point icon={CalendarCheck} title="Ekte ledige tider" text="Du kan bare velge tider vi faktisk har kapasitet til." />
        <Point icon={Sparkles} title="Grundig når det trengs" text="Riktig metode og produkt der hygiene eller smuss krever det." />
      </section>

      <section className="mx-auto max-w-6xl px-5 py-6 grid gap-4 md:grid-cols-2">
        <div className="rounded-3xl border border-stone-200 bg-white p-7">
          <Sparkles className="h-6 w-6 text-[#2F5D50]" />
          <h2 className="mt-3 font-heading text-2xl font-extrabold">Engangsrenhold</h2>
          <p className="mt-2 text-stone-600">Like lett som å bestille mat: velg vask, legg i «kurven», velg tid – så kommer renholderen på døren. Husvask, flyttevask, visningsvask eller Airbnb-vask.</p>
          <Link to="/bestill" data-testid="home-once-card" className="mt-5 inline-block font-bold text-[#2F5D50]">Bestill engangsrenhold →</Link>
        </div>
        <div className="rounded-3xl bg-[#2F5D50] p-7 text-white">
          <Repeat className="h-6 w-6" />
          <h2 className="mt-3 font-heading text-2xl font-extrabold">Fast renholdsavtale</h2>
          <p className="mt-2 text-white/85">Renhold hver uke, annenhver uke eller månedlig – for hjem, kontor eller sameie. Få veiledende pris, så avtaler vi detaljene. Ingenting er bindende før du har godtatt.</p>
          <Link to="/fast-renholdsavtale" data-testid="home-recurring-card" className="mt-5 inline-block font-bold underline underline-offset-4">Få fast renholdsavtale →</Link>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-12">
        <h2 className="font-heading text-3xl font-extrabold text-stone-900">Tjenester</h2>
        <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{cat?.services.map((s) => <ServiceCard key={s.slug} s={s} />)}</div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-12">
        <div className="rounded-3xl bg-[#E7EFE9] p-8 sm:p-12 grid gap-6 lg:grid-cols-[1fr_1.3fr]">
          <h2 className="font-heading text-3xl font-extrabold text-stone-900">Bevisste produktvalg</h2>
          <div className="space-y-3 text-stone-700">
            <p>Der det er egnet, prioriterer vi vann, mikrofiber og enkle metoder fremfor unødvendig bruk av sterke rengjøringsmidler. Du velger om vi tar med produkter, eller om vi skal bruke dine.</p>
            <Link to="/renhold-og-helse" className="font-semibold text-[#2F5D50] underline">Les om renhold og helse</Link>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-12" data-testid="home-kundepilot-section">
        <div className="rounded-3xl border border-stone-200 bg-white p-8 sm:p-10">
          <span className="text-xs font-bold tracking-wide text-[#2F5D50]">FOR RENHOLDSBEDRIFTER</span>
          <h2 className="mt-2 font-heading text-3xl font-extrabold text-stone-900">Kundepilot – finn nye bedriftskunder</h2>
          <div className="mt-6 grid gap-5 md:grid-cols-3">
            {[[MapPin, "Søk i ditt område", "Velg sted og radius, så finner vi kontorer, sameier og butikker i nærheten."],
              [Mail, "Ferdige e-postutkast", "Få personlige utkast til hver bedrift – du godkjenner alt før noe sendes."],
              [Send, "Send fra egen Gmail", "Send alle godkjente e-poster på én gang, fra din egen adresse."]].map(([Icon, t, d]) => (
              <div key={t} className="flex gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#E7EFE9] text-[#2F5D50]"><Icon className="h-5 w-5" /></div>
                <div><h3 className="font-bold text-stone-900">{t}</h3><p className="text-sm text-stone-600">{d}</p></div>
              </div>
            ))}
          </div>
          <Link to="/prospect-finder" data-testid="home-kundepilot-link" className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#2F5D50] px-6 py-3 font-bold text-white hover:bg-[#244a40] transition-colors">
            Les mer om Kundepilot <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </main>
  );
}
